import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import mainLogo from '../../../assets/Logo/main_logo.png';
import eyeOffIcon from '../../../assets/Icon/project/JoinMembershiIcons.png';
import backgroundImage from '../../../assets/img/background.png';
import { useSignup } from '../useSignup';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '../../../hooks/useClickOutside';
import {
  MISSIONFUND_PRIVACY_POLICY_URL,
  MISSIONFUND_TERMS_OF_SERVICE_URL,
} from '../../../Utils/missionFundLegalUrls';

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 198px;
  width: 100%;
`;

const FooterLegalLink = styled.a`
  font-family: 'Pretendard', sans-serif;
  ${({ theme }) => theme.typography.chip.medium16}
  color: #929292;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease-in-out;
  &:hover {
    color: #17171b;
    text-decoration: underline;
  }
`;

const FooterLinkDivider = styled.span`
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 300;
  color: #bcbcbc;
  line-height: 1;
  &::before {
    content: '|';
  }
`;

const SignupWeb = () => {
  const { t } = useTranslation('translation');
  const {
    step,
    fundraiserType,
    setFundraiserType,
    email,
    setEmail,
    password,
    setPassword,
    passwordConfirm,
    setPasswordConfirm,
    name,
    setName,
    phoneNumber,
    setPhoneNumber,
    category,
    setCategory,
    handleNext,
    handleConfirm,
    isSubmitting,
    error,
    emailCheckStatus,
    emailCheckMessage,
    isEmailAvailable,
    passwordMessage,
    goToAccount,
  } = useSignup();

  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);

  const renderStep0 = () => (
    <>
      <Title>{t('page.signup.step0.title')}</Title>
      <RadioGroup>
        <RadioOption active={fundraiserType === 'individual'} onClick={() => setFundraiserType('individual')}>
          <RadioCircle active={fundraiserType === 'individual'} />
          <RadioLabel active={fundraiserType === 'individual'}>{t('page.signup.step0.types.individual')}</RadioLabel>
        </RadioOption>
        <RadioOption active={fundraiserType === 'group'} onClick={() => setFundraiserType('group')}>
          <RadioCircle active={fundraiserType === 'group'} />
          <RadioLabel active={fundraiserType === 'group'}>{t('page.signup.step0.types.group')}</RadioLabel>
        </RadioOption>
      </RadioGroup>
      <NextButton onClick={handleNext}>{t('common.actions.next')}</NextButton>
    </>
  );

  const renderStep1 = () => (
    <>
      <Stepper activeStep={1} />
      <Title>
        {t('page.signup.step1.titleLine1')}<br />{t('page.signup.step1.titleLine2')}
      </Title>
      <InputWrapper>
        <InputWithIcon>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('page.signup.step1.emailPlaceholder')}
          />
          {email && (
            <ClearButton type="button" onClick={() => setEmail('')}>
              <ClearIcon />
            </ClearButton>
          )}
        </InputWithIcon>
        <HelperText>• {t('page.signup.step1.helperLine1')} {t('page.signup.step1.helperLine2')}</HelperText>
        {emailCheckStatus !== 'idle' && emailCheckMessage && (
          <EmailCheckText $status={emailCheckStatus}>
            {emailCheckStatus === 'checking' && <EmailCheckSpinner />}
            {emailCheckMessage}
          </EmailCheckText>
        )}
      </InputWrapper>
      <NextButton onClick={handleNext} disabled={!email || emailCheckStatus === 'checking' || !isEmailAvailable}>
        {t('common.actions.next')}
      </NextButton>
    </>
  );

  const renderStep2 = () => (
    <>
      <Stepper activeStep={2} />
      <Title>
        {t('page.signup.step2.titleLine1')}<br />{t('page.signup.step2.titleLine2')}
      </Title>
      <InfoBox>
        <InfoLabel>{t('page.signup.step2.accountLabel')}</InfoLabel>
        <InfoValue>{email}</InfoValue>
      </InfoBox>
      <InputWrapper>
        <InputWithIcon>
          <Input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('page.signup.step2.passwordPlaceholder')}
          />
          {password && (
            <InputActions>
              <TogglePasswordButton type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeIcon /> : <EyeOffIcon />}
              </TogglePasswordButton>
              <ClearButton type="button" onClick={() => setPassword('')}>
                <ClearIcon />
              </ClearButton>
            </InputActions>
          )}
        </InputWithIcon>
      </InputWrapper>
      <InputWrapper style={{ marginTop: '-6px' }}>
        <InputWithIcon>
          <Input
            type={showPasswordConfirm ? 'text' : 'password'}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            placeholder={t('page.signup.step2.passwordConfirmPlaceholder')}
          />
          {passwordConfirm && (
            <InputActions>
              <TogglePasswordButton type="button" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}>
                {showPasswordConfirm ? <EyeIcon /> : <EyeOffIcon />}
              </TogglePasswordButton>
              <ClearButton type="button" onClick={() => setPasswordConfirm('')}>
                <ClearIcon />
              </ClearButton>
            </InputActions>
          )}
        </InputWithIcon>
        {passwordMessage && <PasswordErrorText>{passwordMessage}</PasswordErrorText>}
      </InputWrapper>
      <NextButton onClick={handleNext} disabled={!password || password !== passwordConfirm}>{t('common.actions.next')}</NextButton>
    </>
  );

  const [isSelectOpen, setIsSelectOpen] = React.useState(false);
  const categorySelectRef = useRef<HTMLDivElement>(null);

  const closeSignupSelects = useCallback(() => {
    setIsSelectOpen(false);
  }, []);
  useClickOutside(closeSignupSelects, categorySelectRef);

  const renderStep3 = () => {
    const individualOptions = [
      { value: 'Pastor', label: t('page.signup.step3.categories.churchWorker') },
      { value: 'Missionary', label: t('page.signup.step3.categories.missionary') },
      { value: 'Staff', label: t('page.signup.step3.categories.organizationWorker') },
      { value: 'Private', label: t('page.signup.step3.categories.individualEtc') },
    ];

    const groupOptions = [
      { value: 'NGO', label: t('page.signup.step3.categories.nonProfit') },
      { value: 'Church', label: t('page.myAccount.userType.church') },
      { value: 'Orphanage', label: t('page.signup.step3.categories.orphanage') },
      { value: 'SmallGroup', label: t('page.signup.step3.categories.prison') },
      { value: 'MissionSchool', label: t('page.signup.step3.categories.school') },
      { value: 'ETC', label: t('page.signup.step3.categories.groupEtc') },
    ];

    const currentOptions = fundraiserType === 'individual' ? individualOptions : groupOptions;
    const selectedLabel = currentOptions.find(opt => opt.value === category)?.label || t('page.signup.step3.categoryPlaceholder');

    return (
      <>
        <Stepper activeStep={3} />
        <TitleWithPadding>
          {fundraiserType === 'individual' ? t('page.signup.step3.titleIndividual') : t('page.signup.step3.titleGroup')}
        </TitleWithPadding>
        <InputFieldsContainer>
          <InputWithIcon>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={fundraiserType === 'individual' ? t('page.signup.step3.namePlaceholderIndividual') : t('page.signup.step3.namePlaceholderGroup')}
            />
            {name && (
              <ClearButton type="button" onClick={() => setName('')}>
                <ClearIcon />
              </ClearButton>
            )}
          </InputWithIcon>
          <InputWithIcon>
            <Input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
              placeholder={fundraiserType === 'individual' ? t('page.signup.step3.phonePlaceholderIndividual') : t('page.signup.step3.phonePlaceholderGroup')}
            />
            {phoneNumber && (
              <ClearButton type="button" onClick={() => setPhoneNumber('')}>
                <ClearIcon />
              </ClearButton>
            )}
          </InputWithIcon>
          <CategorySelectWrapper ref={categorySelectRef}>
            <CustomSelectWrapper isOpen={isSelectOpen} onClick={() => setIsSelectOpen(!isSelectOpen)}>
              <CustomSelectValue hasValue={!!category}>
                {selectedLabel}
                <ChevronIcon isOpen={isSelectOpen} />
              </CustomSelectValue>
              {isSelectOpen && (
                <OptionsList>
                {currentOptions.map((opt) => (
                  <OptionItem
                    key={opt.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCategory(opt.value);
                      setIsSelectOpen(false);
                    }}
                  >
                    {opt.label}
                  </OptionItem>
                ))}
              </OptionsList>
            )}
            </CustomSelectWrapper>
          </CategorySelectWrapper>
        </InputFieldsContainer>
        <NoteSection>
          <NoteText>• {t('page.signup.step3.notes.management')}</NoteText>
          <NoteText>• {t('page.signup.step3.notes.contact')}</NoteText>
        </NoteSection>
        {error && <ErrorText>{error}</ErrorText>}
        <NextButton
          onClick={handleConfirm}
          disabled={isSubmitting || !name || !phoneNumber || !category}
        >
          {isSubmitting ? t('common.actions.processing') : t('common.actions.confirm')}
        </NextButton>
      </>
    );
  };

  const renderStep4 = () => (
    <>
      <Step4Title>{t('page.signup.step4.title')}</Step4Title>
      <WelcomeMessage>
        {t('page.signup.step4.messageWebLine1')}
        <br />
        {t('page.signup.step4.messageWebLine2')}
        <br />
        {t('page.signup.step4.messageWebLine3')}
      </WelcomeMessage>
      <ProfileSection>
        <ProfileCircle>
          <svg width="90" height="90" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#2BC2E1" />
          </svg>
        </ProfileCircle>
        <ProfileInfo>
          <ProfileEmail>{email}</ProfileEmail>
          <ProfileName>{name}</ProfileName>
        </ProfileInfo>
      </ProfileSection>
      <ButtonSection>
        <StartButton onClick={goToAccount}>
          {t('common.actions.start')}
        </StartButton>
        <SkipLink onClick={goToAccount}>
          {t('common.actions.skipLater')}
        </SkipLink>
      </ButtonSection>
    </>
  );

  return (
    <WebPageLayout title="" hideHeader hideFooter>
      <BackgroundOverlay />
      <Centered>
        <SignupContainer>
          <LogoSection>
            <Logo src={mainLogo} alt="Mission Fund" />
          </LogoSection>

          <ContentSection>
            {step === 0 && renderStep0()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </ContentSection>

          <FooterLinks>
            <FooterLegalLink
              href={MISSIONFUND_TERMS_OF_SERVICE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.footer.servicePolicy')}
            </FooterLegalLink>
            <FooterLinkDivider />
            <FooterLegalLink
              href={MISSIONFUND_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.footer.privacyPolicy')}
            </FooterLegalLink>
          </FooterLinks>
        </SignupContainer>
      </Centered>
    </WebPageLayout>
  );
};

const Stepper = ({ activeStep }: { activeStep: number }) => (
  <StepperWrapper>
    <StepLineBackground />
    <StepCirclesContainer>
      <StepCircle active={activeStep === 1}>1</StepCircle>
      <StepCircle active={activeStep === 2}>2</StepCircle>
      <StepCircle active={activeStep === 3}>3</StepCircle>
    </StepCirclesContainer>
  </StepperWrapper>
);

const StepperWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const StepLineBackground = styled.div`
  position: absolute;
  width: 133px;
  height: 1px;
  background: #F0F1F2;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
`;

const StepCirclesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 44px;
  position: relative;
  z-index: 1;
`;

const StepCircle = styled.div<{ active: boolean }>`
  width: ${({ active }) => (active ? '30px' : '20px')};
  height: ${({ active }) => (active ? '30px' : '20px')};
  border-radius: 50%;
  background: ${({ active }) => (active ? '#2BC2E1' : '#F0F1F2')};
  color: white;
  font-family: 'Pretendard', sans-serif;
  font-size: ${({ active }) => (active ? '20px' : '12px')};
  font-weight: 400;
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
  pointer-events: none;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 0;
  width: 100%;
  min-height: 100vh;
  position: relative;
  z-index: 1;
`;

const SignupContainer = styled.div`
  width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100vh - 160px);
  gap: 22px;
  position: relative;
`;

const LogoSection = styled.div`
  width: 272px;
  height: 85px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Logo = styled.img`
  width: 252px;
  height: 65px;
  object-fit: contain;
`;

const ContentSection = styled.div`
  width: 100%;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[10]};
  border-radius: 10px;
  padding: 44px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  min-height: 381px;
  box-sizing: border-box;
`;

const Title = styled.h2`
  font-family: 'Pretendard', sans-serif;
  font-size: 32px;
  font-weight: 400;
  color: #17171B;
  margin-bottom: 0;
  text-align: left;
  line-height: 1.4;
`;

const TitleWithPadding = styled(Title)`
  padding-bottom: 30px;
  margin-bottom: -30px;
`;

const RadioGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 0;
  margin-top: 30px;
`;

const RadioOption = styled.div<{ active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0;
  cursor: pointer;
`;

const RadioCircle = styled.div<{ active: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1.5px solid #E5E5E5;
  background: #ffffff;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primaryBlue[40]};
    transform: ${({ active }) =>
      active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const RadioLabel = styled.span<{ active: boolean }>`
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: ${({ active }) => (active ? 500 : 400)};
  line-height: 1.5;
  color: ${({ active, theme }) => (active ? theme.colors.grayScale[90] : theme.colors.grayScale[60])};
`;

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const InputFieldsContainer = styled.div`
  width: 512px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: stretch;
  margin-top: 60px;
`;

const CategorySelectWrapper = styled.div`
  width: 100%;
`;

const InputWithIcon = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #BCBCBC;
  padding-bottom: 8px;

  &:focus-within {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primaryBlue[40]};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0;
  border: none;
  font-family: 'Pretendard', sans-serif;
  ${({ theme }) => theme.typography.contents.regular24};
  color: ${({ theme }) => theme.colors.grayScale[70]};
  outline: none;
  flex: 1;
  background: transparent;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grayScale[70]};
  }

  &:focus {
    color: ${({ theme }) => theme.colors.grayScale[90]};
  }

  &:not(:focus) {
    color: ${({ theme }) => theme.colors.grayScale[70]};
  }
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClearButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
`;

const ClearIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#BCBCBC" />
    <path d="M8 8L16 16M16 8L8 16" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TogglePasswordButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
`;

const EyeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5ZM12 9.5C10.34 9.5 9 10.84 9 12.5C9 14.16 10.34 15.5 12 15.5C13.66 15.5 15 14.16 15 12.5C15 10.84 13.66 9.5 12 9.5Z" fill="#676767" />
  </svg>
);

const EyeOffIcon = () => (
  <img src={eyeOffIcon} alt="hide password" width="24" height="24" />
);

const HelperText = styled.div`
  font-size: 20px;
  margin-top: 30px;
`;

const ErrorText = styled.div`
  width: 100%;
  margin-top: 16px;
  font-size: 14px;
  color: #d33;
`;

const PasswordErrorText = styled.div`
  color: var(--Red-10, #F00);
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin-top: 8px;
`;

const EmailCheckText = styled.div<{ $status: 'checking' | 'available' | 'duplicate' | 'error' | 'idle' }>`
  margin-top: 12px;
  font-size: 16px;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ $status }) => ($status === 'available' ? '#0B6773' : $status === 'checking' ? '#676767' : '#d33')};
`;

const EmailCheckSpinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid #d9d9d9;
  border-top-color: #676767;
  border-radius: 50%;
  animation: signupEmailSpin 0.8s linear infinite;

  @keyframes signupEmailSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const InfoBox = styled.div`
  width: 100%;
  padding: 0;
  margin-top: 30px;
  margin-bottom: 0;
`;

const InfoLabel = styled.div`
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.5;
  color: #676767;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.5;
  color: #17171B;
`;

const CustomSelectWrapper = styled.div<{ isOpen?: boolean }>`
  width: 100%;
  position: relative;
  cursor: pointer;
  border-bottom: 1px solid ${({ isOpen, theme }) => (isOpen ? theme.colors.primaryBlue[40] : '#BCBCBC')};
  padding-bottom: 8px;
  transition: border-color 0.2s;

  &:focus-within {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primaryBlue[40]};
  }
`;

const CustomSelectValue = styled.div<{ hasValue: boolean }>`
  width: 100%;
  padding: 0;
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
`;

const ChevronIcon = styled.div<{ isOpen: boolean }>`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9L12 15L18 9' stroke='%23191919' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  transition: transform 0.2s;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const OptionsList = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 5px;
  box-shadow: 0px 4px 8.8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  padding: 8px 12px;
`;

const OptionItem = styled.div`
  padding: 10px 12px;
  font-family: 'Pretendard', sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.5;
  color: #17171B;
  cursor: pointer;
  background: white;
  border-radius: 5px;

  &:hover {
    background: #EEEEEE;
  }
`;

const NoteSection = styled.div`
  width: 512px;
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: flex-start;
`;

const NoteText = styled.div`
  font-family: 'Pretendard', sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.5;
  color: #000000;
`;

const Step4Title = styled.h2`
  font-family: 'Pretendard', sans-serif;
  font-size: 32px;
  font-weight: 400;
  color: #17171B;
  line-height: 1.4;
  text-align: center;
  width: 361px;
  margin: 0;
  align-self: center;
`;

const WelcomeMessage = styled.p`
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 400;
  text-align: center;
  line-height: 1.5;
  width: 512px;
  color: #17171B;
  margin: 0;
  align-self: center;
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const ProfileCircle = styled.div`
  width: 124px;
  height: 124px;
  border-radius: 48px;
  background: #f0fbff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 512px;
`;

const ProfileName = styled.div`
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.5;
  color: #17171B;
`;

const ProfileEmail = styled.div`
  font-family: 'Pretendard', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.5;
  color: #17171B;
`;

const NextButton = styled.button`
  width: 512px;
  height: 62px;
  align-self: center;
  margin: 0 auto;
  background: ${({ disabled }) => (disabled ? '#BCBCBC' : '#026478')};
  color: ${({ disabled }) => (disabled ? '#EEEEEE' : '#FFFFFF')};
  border: none;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.5;
  font-family: 'Pretendard', sans-serif;
  letter-spacing: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background: #014d5c;
    transform: translateY(-1px);
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`;

const StartButton = styled.button`
  width: 512px;
  height: 62px;
  background: #026478;
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.5;
  font-family: 'Pretendard', sans-serif;
  letter-spacing: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 10px 24px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: #014d5c;
    transform: translateY(-1px);
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SkipLink = styled.button`
  background: none;
  border: none;
  font-family: 'Pretendard', sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.5;
  color: #929292;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #676767;
  }
`;

export default SignupWeb;
